import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PermissionsService } from '../../../shared/services/permissions.service';
import {
  ApplicationPermissionConfig,
  ApplicationsConfig
} from '../../../shared/interface/applications-config';

type ApplicationPermissionAssignmentContext = 'user' | 'department' | 'all';

@Component({
  selector: 'app-application-permission',
  templateUrl: './application-permission.component.html',
  styleUrls: ['./application-permission.component.scss']
})
export class ApplicationPermissionComponent implements OnInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() applicationPermissionsForm: FormGroup;
  @Input() currentApplications: any = {};
  @Input() currentApplicationPermissions: any = {};
  @Input() disabled = false;

  /**
   * user       - pokazuje uprawnienia możliwe do ustawienia na użytkowniku
   * department - pokazuje tylko uprawnienia możliwe do ustawienia na dziale
   * all        - pokazuje wszystko, np. podgląd techniczny
   */
  @Input() assignmentContext: ApplicationPermissionAssignmentContext = 'all';

  applicationsConfig?: ApplicationsConfig;
  currentApplicationPermissionsMap: any = {};

  applicationPermissionItems: Array<{
    key: string;
    config: ApplicationPermissionConfig;
  }> = [];

  constructor(
    private permissionsService: PermissionsService,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    if (!this.applicationPermissionsForm) {
      this.applicationPermissionsForm = this.fb.group({});
    }

    this.currentApplicationPermissionsMap = this.normalizeCurrentApplicationPermissions(
      this.currentApplicationPermissions
    );

    this.permissionsService.getApplicationsConfig()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((applicationsConfig: ApplicationsConfig) => {
        this.applicationsConfig = applicationsConfig;

        const applicationPermissions = applicationsConfig.applicationPermissions || {};

        this.applicationPermissionItems = Object.keys(applicationPermissions)
          .map((key) => {
            return {
              key,
              config: {
                ...applicationPermissions[key],
                permissions: this.filterPermissionsByAssignmentContext(
                  applicationPermissions[key].permissions || []
                ),
              },
            };
          })
          .filter((item) => item.config.permissions.length > 0);

        this.applicationPermissionItems.forEach((item) => {
          this.prepareApplicationFormGroup(item.key, item.config);
        });
      });
  }

  private filterPermissionsByAssignmentContext(permissions: any[]): any[] {
    if (this.assignmentContext === 'all') {
      return permissions;
    }

    if (this.assignmentContext === 'department') {
      return permissions.filter((permission) => {
        return (permission.assignmentMode ?? 'department_or_user') === 'department_or_user';
      });
    }

    /**
     * Dla użytkownika pokazujemy:
     * - individual, bo musi być ustawione indywidualnie,
     * - department_or_user, bo użytkownik może mieć nadpisanie indywidualne.
     */
    return permissions.filter((permission) => {
      const assignmentMode = permission.assignmentMode ?? 'department_or_user';

      return assignmentMode === 'individual'
        || assignmentMode === 'department_or_user';
    });
  }

  private normalizeCurrentApplicationPermissions(value: any): any {
    if (!value) {
      return {};
    }

    if (!Array.isArray(value)) {
      return value;
    }

    return value.reduce((result, item) => {
      if (item.applicationKey) {
        result[item.applicationKey] = item.permissions || {};
      }

      return result;
    }, {});
  }

  private prepareApplicationFormGroup(
    applicationKey: string,
    config: ApplicationPermissionConfig
  ): void {
    if (!this.applicationPermissionsForm.get(applicationKey)) {
      this.applicationPermissionsForm.addControl(
        applicationKey,
        this.fb.group({})
      );
    }

    const applicationGroup = this.applicationPermissionsForm.get(applicationKey) as FormGroup;

    config.permissions.forEach((permission) => {
      if (!applicationGroup.get(permission.module)) {
        applicationGroup.addControl(
          permission.module,
          this.fb.control(
            this.currentApplicationPermissionsMap?.[applicationKey]?.[permission.module] ?? 'none'
          )
        );
      }
    });
  }

  getApplicationGroup(applicationKey: string): FormGroup {
    return this.applicationPermissionsForm.get(applicationKey) as FormGroup;
  }

  getRoleLabel(config: ApplicationPermissionConfig, role: string): string {
    return config.roles?.[role] ?? role;
  }

  getPermissionValue(applicationKey: string, module: string): string {
    return this.currentApplicationPermissionsMap?.[applicationKey]?.[module] ?? 'none';
  }

  getApplicationName(item: { key: string; config: ApplicationPermissionConfig }): string {
    return item.config.application?.name || item.key;
  }

  getAssignmentModeLabel(permission: any): string {
    const assignmentMode = permission.assignmentMode ?? 'department_or_user';

    if (assignmentMode === 'individual') {
      return 'Nie dziedziczy z działu';
    }

    return 'Dział lub użytkownik';
  }

  isIndividualPermission(permission: any): boolean {
    return permission.assignmentMode === 'individual';
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
